import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal, UserPlus, Download, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { StatusPill } from "@/components/common/StatusPill";
import { UserAvatar } from "@/components/common/UserAvatar";
import { FormAlert } from "@/components/common/Alert";
import { ErrorState } from "@/components/common/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AccessDialog } from "@/pages/admin/AccessDialog";

const RoleSelect = ({ value, onChange, disabled, testid, id }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger id={id} className="h-9 w-[130px] rounded-xl bg-card" data-testid={testid} aria-label="Role"><SelectValue /></SelectTrigger>
    <SelectContent className="rounded-xl">
      {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-option-${v}`}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const InviteDialog = ({ open, onOpenChange, onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", role: "member", external: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/users/invite", form);
      toast[data.email_sent ? "success" : "warning"](data.email_sent ? `Invitation sent to ${form.email}` : "Invitation created, but the email did not send. Try resending.");
      setForm({ name: "", email: "", role: "member", external: false });
      onOpenChange(false);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite someone</DialogTitle>
          <DialogDescription>They get an email with a set-password link that works once and expires in 48 hours.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormAlert testid="invite-error">{error}</FormAlert>
          <div className="space-y-2"><Label htmlFor="invite-name">Name</Label><Input id="invite-name" value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" /></div>
          <div className="space-y-2"><Label htmlFor="invite-email">Work email</Label><Input id="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@fuse-capital.com" data-testid="invite-email-input" /></div>
          <div className="space-y-2"><Label htmlFor="invite-role">Role</Label><RoleSelect id="invite-role" value={form.role} onChange={(role) => setForm({ ...form, role })} testid="invite-role-select" /></div>
          <div className="flex items-start gap-3 rounded-xl border p-3">
            <Checkbox id="invite-external" checked={form.external} onCheckedChange={(v) => setForm({ ...form, external: v === true })} data-testid="invite-external-checkbox" />
            <div><Label htmlFor="invite-external">External address</Label><p className="text-xs text-muted-foreground">Tick to invite an address outside fuse-capital.com. This is recorded in the audit log.</p></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="invite-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy || !form.name || !form.email} data-testid="invite-submit-button">{busy ? "Sending" : "Send invitation"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DeactivateDialog = ({ target, onClose, onDone }) => {
  const [reassign, setReassign] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/users/${target.id}/deactivate`, { reassign });
      setResult(data);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const close = () => { setResult(null); setReassign(false); setError(""); onClose(); };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-xl" data-testid="deactivate-dialog">
        <DialogHeader>
          <DialogTitle>{result ? "User deactivated" : `Deactivate ${target?.name}`}</DialogTitle>
          <DialogDescription>{result ? "Revoke this user ID in MindPal too, so their MindPal history is closed off." : "This signs them out, blocks sign-in and deletes their favourites and run history. Their prompts and feedback stay."}</DialogDescription>
        </DialogHeader>
        <FormAlert testid="deactivate-error">{error}</FormAlert>
        {result ? (
          <div className="flex items-center gap-2 rounded-xl bg-muted p-3">
            <code className="flex-1 break-all text-sm" data-testid="deactivated-user-id">{result.user_id}</code>
            <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard.writeText(result.user_id); toast.success("User ID copied"); }} data-testid="copy-user-id-button"><Copy className="!h-4 !w-4" /> Copy</Button>
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-xl border p-3">
            <Checkbox id="reassign" checked={reassign} onCheckedChange={(v) => setReassign(v === true)} data-testid="deactivate-reassign-checkbox" />
            <Label htmlFor="reassign" className="font-normal leading-snug">Show their prompts and feedback as from "a former team member"</Label>
          </div>
        )}
        <DialogFooter>
          {result ? <Button onClick={close} data-testid="deactivate-done-button">Done</Button> : (
            <>
              <Button variant="ghost" onClick={close} data-testid="deactivate-cancel-button">Cancel</Button>
              <Button variant="destructive" onClick={confirm} disabled={busy} data-testid="deactivate-confirm-button">{busy ? "Deactivating" : "Deactivate"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FILTERS = ["all", "active", "pending", "deactivated"];

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(null);
  const [accessFor, setAccessFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
      setError("");
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, success) => {
    try {
      const { data } = await fn();
      toast.success(typeof success === "function" ? success(data) : success);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const shown = (users || []).filter((u) => filter === "all" || u.status === filter);

  return (
    <div data-testid="admin-users">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label="Filter users">
          {FILTERS.map((f) => (
            <button key={f} role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} data-testid={`users-filter-${f}`}
              className={cn("rounded-lg px-3 py-1.5 text-sm capitalize transition-quick", filter === f ? "bg-card font-medium shadow-sm" : "text-muted-foreground hover:text-foreground")}>
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => downloadFile("/admin/export/users", "users.csv").catch((e) => toast.error(errorMessage(e)))} data-testid="users-export-button"><Download className="!h-4 !w-4" /> Export CSV</Button>
          <Button onClick={() => setInviteOpen(true)} data-testid="users-invite-button"><UserPlus className="!h-4 !w-4" /> Invite</Button>
        </div>
      </div>
      {error ? <ErrorState message={error} onRetry={load} /> : !users ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : (
        <div className="card-elevated overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Person</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last sign-in</TableHead><TableHead><span className="sr-only">Actions</span></TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No users in this view.</TableCell></TableRow>}
              {shown.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar user={u} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{u.name}{u.id === me.id && <span className="ml-2 text-xs text-muted-foreground">You</span>}</p>
                        <p className="truncate text-xs text-muted-foreground">{u.email}{u.external && " · External"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><RoleSelect value={u.role} disabled={u.id === me.id || u.status === "deactivated"} onChange={(role) => act(() => api.patch(`/admin/users/${u.id}/role`, { role }), `Role changed to ${ROLE_LABELS[role]}`)} testid={`user-role-select-${u.email}`} /></TableCell>
                  <TableCell>
                    <StatusPill status={u.status} testid={`user-status-${u.email}`} />
                    {u.locked_until && new Date(u.locked_until) > new Date() && <span className="ml-2 text-xs text-warning">Locked</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(u.last_sign_in_at)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label={`Actions for ${u.name}`} data-testid={`user-actions-${u.email}`}><MoreHorizontal /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        {u.status === "pending" && <DropdownMenuItem onSelect={() => act(() => api.post(`/admin/users/${u.id}/resend-invite`), "Invitation resent")} data-testid="user-action-resend">Resend invitation</DropdownMenuItem>}
                        {u.status === "active" && <DropdownMenuItem onSelect={() => act(() => api.post(`/admin/users/${u.id}/send-reset`), "Password reset email sent")} data-testid="user-action-reset">Send password reset</DropdownMenuItem>}
                        {u.role !== "owner" && <DropdownMenuItem onSelect={() => setAccessFor(u)} data-testid="user-action-access">Agent access</DropdownMenuItem>}
                        <DropdownMenuItem onSelect={() => act(() => api.post(`/admin/users/${u.id}/end-sessions`), (d) => `Ended ${d.ended} session${d.ended === 1 ? "" : "s"}`)} data-testid="user-action-end-sessions">End all sessions</DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => { navigator.clipboard.writeText(u.id); toast.success("User ID copied"); }} data-testid="user-action-copy-id">Copy user ID</DropdownMenuItem>
                        {u.status === "deactivated" && <DropdownMenuItem onSelect={() => act(() => api.post("/admin/users/invite", { email: u.email, name: u.name, role: u.role, external: u.external }), "Invitation sent")} data-testid="user-action-reinvite">Invite again</DropdownMenuItem>}
                        {u.status !== "deactivated" && u.id !== me.id && <DropdownMenuItem className="text-destructive" onSelect={() => setDeactivating(u)} data-testid="user-action-deactivate">Deactivate</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onDone={load} />
      <DeactivateDialog target={deactivating} onClose={() => setDeactivating(null)} onDone={load} />
      <AccessDialog target={accessFor} onClose={() => setAccessFor(null)} onDone={load} />
    </div>
  );
}
